// ============================================================================
//  VOICE PASS SERVICE

// ============================================================================
// Handles 24-hour voice chat passes that users can purchase
// - Unlimited Pass (6 PSS Coins): 24 hours of unlimited voice table creation
// - Regular Pass (2.99 PSS Coins): Single use for one table
// - Pass waives voice chat fee when creating tables (does NOT bypass rake)
// ============================================================================
//  SECURITY: All purchases are handled by Cloud Functions (backend)

// This service only calls the secure backend APIs
// ============================================================================

import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { isIOSVoiceVideoBlocked } from '../utils/iosUtils';

// ============================================================================
// TYPES
// ============================================================================

export interface VoicePass {
  expiresAt: Date;
  type: 'unlimited' | 'regular';
  purchasedAt: Date;
  usageCount: number;
}

export interface VoicePassValidationResult {
  valid: boolean;
  pass?: VoicePass;
  remainingTime?: string;
  error?: string;
}

export interface VoicePassPurchaseResult {
  success: boolean;
  pass?: VoicePass;
  error?: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

// Pricing (in PSS Coins) - must match backend
export const VOICE_PASS_PRICES = {
  unlimited: 6.00,  // $6 - Unlimited 24hr pass
  regular: 2.99,    // $2.99 - Single use
};

// Pass duration
const PASS_DURATION_HOURS = 24;

// ============================================================================
// CLOUD FUNCTIONS
// ============================================================================

const functions = getFunctions(getApp(), 'us-central1');

// Backend API calls
const purchaseUnlimitedPassAPI = httpsCallable(functions, 'purchaseUnlimitedVoicePass');
const getPassStatusAPI = httpsCallable(functions, 'getVoicePassStatus');
const recordUsageAPI = httpsCallable(functions, 'recordVoicePassUsage');
const validatePassAPI = httpsCallable(functions, 'validateVoicePass');

// ============================================================================
// VOICE PASS SERVICE CLASS
// ============================================================================

class VoicePassService {
  // ==========================================================================
  // PASS VALIDATION
  // ==========================================================================

  /**
   * Check if a user has a valid (non-expired) voice pass
   * Calls secure backend API
   */
  async getUserVoicePass(userId: string): Promise<VoicePassValidationResult> {
    try {
      const result = await getPassStatusAPI({});
      const data = result.data as any;

      if (!data.success) {
        return { valid: false, error: data.error || 'Failed to check pass status' };
      }

      if (!data.hasPass) {
        return { valid: false, error: data.expired ? 'Voice pass expired' : 'No active voice pass' };
      }

      return {
        valid: true,
        pass: {
          expiresAt: new Date(data.pass.expiresAt),
          type: data.pass.type || 'unlimited',
          purchasedAt: data.pass.purchasedAt ? new Date(data.pass.purchasedAt) : new Date(),
          usageCount: data.pass.usageCount || 0,
        },
        remainingTime: data.remainingTime,
      };
    } catch (error: any) {
      console.error('❌ Error getting voice pass:', error);
      return { valid: false, error: error.message || 'Failed to check voice pass' };
    }
  }

  /**
   * Validate pass via backend (for table creation)
   */
  async validatePass(): Promise<{ valid: boolean; error?: string }> {
    try {
      const result = await validatePassAPI({});
      const data = result.data as any;
      return {
        valid: data.valid,
        error: data.error,
      };
    } catch (error: any) {
      return { valid: false, error: error.message };
    }
  }

  // ==========================================================================
  // PASS PURCHASE (Secure backend calls)
  // ==========================================================================

  /**
   * Purchase an unlimited voice pass (24 hours)
   *  SECURE: Handled entirely by Cloud Function

   */
  async purchaseUnlimitedPass(userId: string): Promise<VoicePassPurchaseResult> {
    //  iOS: Block purchase - voice chat doesn't work on iOS yet

    if (isIOSVoiceVideoBlocked()) {
      return {
        success: false,
        error: 'Voice chat is not yet available on iOS. This feature is coming soon!',
      };
    }

    try {
      console.log(`🎙️ [VOICE_PASS] Purchasing unlimited pass...`);
      
      const result = await purchaseUnlimitedPassAPI({});
      const data = result.data as any;

      if (!data.success) {
        return { success: false, error: data.error || 'Purchase failed' };
      }

      console.log(`✅ [VOICE_PASS] Purchase successful`);

      return {
        success: true,
        pass: {
          expiresAt: new Date(data.pass.expiresAt),
          type: 'unlimited',
          purchasedAt: new Date(data.pass.purchasedAt),
          usageCount: 0,
        },
      };
    } catch (error: any) {
      console.error('❌ [VOICE_PASS] Purchase error:', error);
      
      // Extract error message from Firebase error
      const errorMessage = error.message || error.details || 'Failed to purchase pass';
      return { success: false, error: errorMessage };
    }
  }

  // ==========================================================================
  // PASS USAGE
  // ==========================================================================

  /**
   * Record usage of a voice pass (called when creating a voice-enabled table)
   *  SECURE: Handled by Cloud Function

   */
  async recordUsage(userId: string, tableId?: string, tableName?: string): Promise<boolean> {
    try {
      const result = await recordUsageAPI({ tableId, tableName });
      const data = result.data as any;
      
      if (data.success) {
        console.log(`📝 [VOICE_PASS] Recorded usage`);
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ Error recording pass usage:', error);
      return false;
    }
  }

  // ==========================================================================
  // UTILITY
  // ==========================================================================

  /**
   * Get formatted price string
   */
  getFormattedPrice(type: 'unlimited' | 'regular'): string {
    return `${VOICE_PASS_PRICES[type].toFixed(2)} PSS Coins`;
  }

  /**
   * Get time until pass expires (formatted string)
   */
  getTimeRemaining(expiresAt: Date): string {
    const now = new Date();
    const remainingMs = expiresAt.getTime() - now.getTime();

    if (remainingMs <= 0) return 'Expired';

    const hours = Math.floor(remainingMs / (1000 * 60 * 60));
    const mins = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${mins}m remaining`;
    }
    return `${mins}m remaining`;
  }
}

// ============================================================================
// EXPORT
// ============================================================================

export const voicePassService = new VoicePassService();
export default voicePassService;